const SENSITIVE_PARAMS = ['access_token', 'token', 'refresh_token', 'magiclink', 'signature', 'expires'];

const SENSITIVE_BODY_KEYS = [
	'password',
	'password_confirmation',
	'current_password',
	'token',
	'access_token',
	'refresh_token',
];

const FILTERED = '[filtered]';

export function scrubSentryUrl(url: string): string {
	try {
		const hasOrigin = /^[a-z][a-z0-9+.-]*:\/\//i.test(url);
		const parsed = new URL(url, 'http://scrub.invalid');
		let changed = false;

		for (const param of SENSITIVE_PARAMS) {
			if (parsed.searchParams.has(param)) {
				parsed.searchParams.set(param, FILTERED);
				changed = true;
			}
		}

		if (!changed) {
			return url;
		}

		return hasOrigin ? parsed.toString() : `${parsed.pathname}${parsed.search}${parsed.hash}`;
	} catch {
		return url;
	}
}

function scrubHeaders(headers?: Record<string, string>): void {
	if (!headers) {
		return;
	}

	for (const name of Object.keys(headers)) {
		const lower = name.toLowerCase();
		if (lower === 'authorization' || lower === 'cookie') {
			headers[name] = FILTERED;
		} else if (lower === 'referer') {
			headers[name] = scrubSentryUrl(headers[name]);
		}
	}
}

function scrubData(data: unknown): unknown {
	if (Array.isArray(data)) {
		return data.map(scrubData);
	}

	if (data && typeof data === 'object') {
		const record = data as Record<string, unknown>;
		for (const key of Object.keys(record)) {
			if (SENSITIVE_BODY_KEYS.includes(key.toLowerCase())) {
				record[key] = FILTERED;
			} else {
				record[key] = scrubData(record[key]);
			}
		}
		return record;
	}

	return data;
}

export function scrubSentryBreadcrumb<T extends { data?: Record<string, unknown> | undefined }>(breadcrumb: T): T {
	const data = breadcrumb.data;
	if (data) {
		for (const key of ['from', 'to', 'url']) {
			if (typeof data[key] === 'string') {
				data[key] = scrubSentryUrl(data[key] as string);
			}
		}
	}

	return breadcrumb;
}

type ScrubbableEvent = {
	request?: {
		url?: string;
		headers?: Record<string, string>;
		data?: unknown;
	};
	breadcrumbs?: Array<{ data?: Record<string, unknown> }>;
};

export function scrubSentryEvent<T extends ScrubbableEvent>(event: T): T {
	if (event.request?.url) {
		event.request.url = scrubSentryUrl(event.request.url);
	}

	scrubHeaders(event.request?.headers);

	if (event.request && event.request.data !== undefined) {
		event.request.data = scrubData(event.request.data);
	}

	if (event.breadcrumbs) {
		for (const breadcrumb of event.breadcrumbs) {
			scrubSentryBreadcrumb(breadcrumb);
		}
	}

	return event;
}
